export type EventHandler = (payload: any) => void;

export class SignalingClient {
  private ws: WebSocket | null = null;
  private token: string = '';
  private roomId: string = '';
  private handlers: Map<string, EventHandler[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 6;
  private isIntentionallyClosed = false;
  private isHttpFallback = false;
  private fallbackTimer: any = null;
  private abortCtrl: AbortController | null = null;

  public onOpen?: () => void;
  public onClose?: () => void;
  public onError?: (err: Event) => void;
  public onReconnecting?: (attempt: number, delayMs: number) => void;
  public onReconnected?: () => void;

  public isConnected(): boolean {
    return (this.ws !== null && this.ws.readyState === WebSocket.OPEN) || this.isHttpFallback;
  }

  public getTransportType(): 'websocket' | 'http-polling' {
    return this.isHttpFallback ? 'http-polling' : 'websocket';
  }

  public connect(token: string, roomId?: string) {
    if (this.isConnected()) {
      return;
    }
    this.token = token;
    this.isIntentionallyClosed = false;

    // Extract room ID
    if (roomId) {
      this.roomId = roomId;
    } else {
      try {
        const parts = token.split('.');
        if (parts.length >= 2) {
          const payload = JSON.parse(atob(parts[0]));
          this.roomId = payload.rid || payload.room_id || '';
        }
      } catch (_) {}
      if (!this.roomId) {
        const m = window.location.pathname.match(/\/room\/([^\/\?#]+)/);
        if (m) this.roomId = m[1];
      }
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const url = `${protocol}//${host}/ws?token=${encodeURIComponent(token)}`;

    // Set aggressive fallback timer: if WebSocket handshake does not complete in 1.8s (e.g. reverse proxy limitation), fallback to HTTP polling
    clearTimeout(this.fallbackTimer);
    this.fallbackTimer = setTimeout(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        console.info('[Signaling] WebSocket connection pending or restricted by reverse proxy, engaging high-speed HTTP polling transport');
        this.activateHttpFallback();
      }
    }, 1800);

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        clearTimeout(this.fallbackTimer);
        const wasReconnecting = this.reconnectAttempts > 0;
        this.reconnectAttempts = 0;
        if (this.onOpen) this.onOpen();
        if (wasReconnecting && this.onReconnected) this.onReconnected();
      };

      this.ws.onmessage = (event) => {
        try {
          const lines = event.data.split('\n');
          for (const line of lines) {
            if (!line.trim()) continue;
            const msg = JSON.parse(line);
            this.dispatchEvent(msg.event, msg.payload);
          }
        } catch (err) {
          console.warn('Failed to parse WebSocket message:', err);
        }
      };

      this.ws.onclose = () => {
        if (!this.isHttpFallback && !this.isIntentionallyClosed) {
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
            if (this.onReconnecting) this.onReconnecting(this.reconnectAttempts, delay);
            setTimeout(() => this.connect(this.token, this.roomId), delay);
          } else {
            this.activateHttpFallback();
          }
        }
      };

      this.ws.onerror = (_err) => {
        if (!this.isHttpFallback && !this.isConnected()) {
          this.activateHttpFallback();
        }
      };
    } catch (_) {
      this.activateHttpFallback();
    }
  }

  private async activateHttpFallback() {
    if (this.isHttpFallback || this.isIntentionallyClosed) return;
    this.isHttpFallback = true;
    clearTimeout(this.fallbackTimer);

    if (this.ws) {
      try { this.ws.close(); } catch (_) {}
      this.ws = null;
    }

    if (this.onOpen) this.onOpen();

    this.abortCtrl = new AbortController();
    this.runHttpPollLoop();
  }

  private async runHttpPollLoop() {
    while (!this.isIntentionallyClosed && this.isHttpFallback) {
      try {
        const url = `/api/v1/meetings/${this.roomId}/poll?token=${encodeURIComponent(this.token)}`;
        const res = await fetch(url, {
          signal: this.abortCtrl?.signal,
          headers: { 'Cache-Control': 'no-cache' }
        });

        if (res.ok) {
          const list = await res.json();
          if (Array.isArray(list)) {
            for (const msg of list) {
              if (msg && msg.event) {
                this.dispatchEvent(msg.event, msg.payload);
              }
            }
          }
        } else if (res.status === 401 || res.status === 404) {
          console.warn('[Signaling] HTTP polling unauthorized or meeting ended');
          if (this.onClose) this.onClose();
          break;
        } else {
          await new Promise(r => setTimeout(r, 1200));
        }
      } catch (err: any) {
        if (this.isIntentionallyClosed) break;
        await new Promise(r => setTimeout(r, 1200));
      }
    }
  }

  public reconnectNow() {
    this.close();
    this.isIntentionallyClosed = false;
    this.connect(this.token, this.roomId);
  }

  public send(event: string, payload?: any) {
    const frame = {
      version: '1.0',
      type: 'event',
      event: event,
      payload: payload || {},
      timestamp: Math.floor(Date.now() / 1000),
    };

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
      return;
    }

    // HTTP Dispatch fallback
    if (this.roomId) {
      fetch(`/api/v1/meetings/${this.roomId}/signal?token=${encodeURIComponent(this.token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(frame),
      }).catch(err => {
        console.warn(`[Signaling] Failed to send signal ${event} over HTTP:`, err);
      });
    }
  }

  public on(event: string, handler: EventHandler) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, []);
    }
    this.handlers.get(event)!.push(handler);
  }

  public off(event: string, handler: EventHandler) {
    const list = this.handlers.get(event);
    if (!list) return;
    this.handlers.set(event, list.filter(h => h !== handler));
  }

  private dispatchEvent(event: string, payload: any) {
    const list = this.handlers.get(event);
    if (list) {
      for (const h of list) {
        try {
          h(payload);
        } catch (err) {
          console.error(`Error in signaling handler for ${event}:`, err);
        }
      }
    }
  }

  public blockReconnect() {
    this.isIntentionallyClosed = true;
    this.reconnectAttempts = this.maxReconnectAttempts + 1;
    this.close();
  }

  public close() {
    this.isIntentionallyClosed = true;
    clearTimeout(this.fallbackTimer);
    if (this.abortCtrl) {
      this.abortCtrl.abort();
      this.abortCtrl = null;
    }
    if (this.ws) {
      try { this.ws.close(); } catch (_) {}
      this.ws = null;
    }
    this.isHttpFallback = false;
  }
}
